import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star } from "lucide-react";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";

/* ------------------------------------------------------------------ */
/*  Testimonial placeholders (unchanged — real reviews to replace)     */
/* ------------------------------------------------------------------ */
const TESTIMONIALS = [
  {
    id: "t1",
    name: "Emma Hayes",
    role: "Corporate Traveller",
    quote:
      "Booked them for an early flight to Melbourne. Driver arrived ten minutes early, helped with my bags, and got me to the airport stress-free. Will use again.",
  },
  {
    id: "t2",
    name: "James & Priya",
    role: "Wedding Couple · Hunter Valley",
    quote:
      "They handled our wedding day transport beautifully. Punctual, smartly dressed, and the car was immaculate. Took so much pressure off the day.",
  },
  {
    id: "t3",
    name: "Mark Thompson",
    role: "Operations Manager",
    quote:
      "We've moved our corporate transfers to Sydney James and haven't looked back. Reliable, professional, and the pricing is honest.",
  },
  {
    id: "t4",
    name: "Sophie Lin",
    role: "Family of Five · Cruise Transfer",
    quote:
      "Plenty of room for our luggage and the kids loved the van. Driver knew exactly where to drop us at the terminal. Smooth start to finish.",
  },
];

const PRINCIPLES = [
  {
    n: "01",
    label: "Punctuality",
    body: "Fifteen minutes ahead of every scheduled pickup, without exception.",
  },
  {
    n: "02",
    label: "Preparation",
    body: "Routes planned around traffic patterns, terminals studied, contingencies considered.",
  },
  {
    n: "03",
    label: "Discretion",
    body: "Private conversations remain private. Chauffeurs work quietly and without imposition.",
  },
  {
    n: "04",
    label: "Communication",
    body: "Clear, considered updates — never more, never less than the passenger requires.",
  },
  {
    n: "05",
    label: "Consistency",
    body: "The same standard every journey, whether it's a boardroom transfer or a Hunter Valley weekend.",
  },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="bg-ivory">
      <Navbar />
      <AboutHero />
      <FounderStory />
      <PrinciplesSection />
      <TestimonialsEditorial />
      <AboutClosing />
      <Footer />
      <FloatingActions />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Hero — asymmetric ivory split (image left, editorial copy right)   */
/* ------------------------------------------------------------------ */
function AboutHero() {
  return (
    <section
      data-testid="about-hero-section"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 bg-ivory"
    >
      <div className="max-w-[92rem] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
        {/* Image */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-6 relative"
        >
          <div className="relative overflow-hidden bg-stone">
            <img
              src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjaGF1ZmZldXIlMjBkcml2ZXJ8ZW58MHx8fHwxNzgxODM5MDAzfDA&ixlib=rb-4.1.0&q=85"
              alt="Chauffeur opening a vehicle door"
              className="w-full h-[520px] md:h-[640px] object-cover sjc-slow-reveal"
              loading="eager"
            />
          </div>
          {/* Cinematic experience card overlapping the image */}
          <div
            data-testid="about-experience-card"
            className="absolute -bottom-6 -right-4 md:-right-8 bg-midnight text-warm-white px-7 py-6 max-w-[16rem]"
          >
            <p className="sjc-eyebrow text-gold">Est. 2012</p>
            <p className="mt-2 font-display text-xl md:text-2xl leading-tight">
              Twelve years quietly moving Sydney.
            </p>
          </div>
        </motion.div>

        {/* Copy */}
        <div className="lg:col-span-6 lg:pl-8">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
            className="sjc-eyebrow text-muted-light flex items-center gap-4"
          >
            <span className="sjc-hairline w-8" />
            About Sydney James
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            data-testid="about-heading"
            className="font-display mt-6 text-charcoal text-4xl sm:text-5xl md:text-6xl lg:text-[76px] leading-[1.02] tracking-[-0.02em]"
          >
            A private chauffeur service,{" "}
            <span className="italic font-light">
              built around one idea.
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 text-muted-light text-lg md:text-xl leading-[1.7] font-light max-w-lg"
          >
            That excellent chauffeur travel comes not from cars or uniforms,
            but from the quiet accumulation of experience — years spent understanding
            what discerning passengers actually value.
          </motion.p>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Founder / experience story — dark editorial section                */
/* ------------------------------------------------------------------ */
function FounderStory() {
  return (
    <section
      data-testid="about-story-section"
      className="bg-midnight text-warm-white py-24 md:py-36 border-t border-white/10"
    >
      <div className="max-w-[92rem] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-14 lg:gap-24">
        <div className="lg:col-span-4">
          <p className="sjc-eyebrow text-gold flex items-center gap-4">
            <span className="sjc-hairline w-8" />
            The Story
          </p>
          <h2 className="font-display mt-6 text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.015em]">
            Sydney-owned.
            <br />
            <span className="italic font-light">Sydney-operated.</span>
          </h2>
        </div>

        <div className="lg:col-span-8 space-y-8 text-warm-white/70 text-lg md:text-xl font-light leading-[1.75] max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            Sydney James began with a straightforward observation: the difference between
            an ordinary chauffeur service and a truly exceptional one is measured in the details
            other operators overlook.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            Arriving early rather than on time. Knowing which terminal doors close at which
            hour. Understanding that the return leg of a wedding matters as much as the arrival.
            Anticipating a delayed flight before the passenger has landed.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            Today we look after airport transfers, corporate travel, weddings, hotel pickups
            and regional journeys for a discerning group of clients across Greater Sydney.
            The scale has grown; the philosophy has not.
          </motion.p>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Principles — 5 restrained editorial pillars                        */
/* ------------------------------------------------------------------ */
function PrinciplesSection() {
  return (
    <section
      data-testid="about-principles-section"
      className="bg-ivory py-24 md:py-36 border-t border-charcoal/10"
    >
      <div className="max-w-[92rem] mx-auto px-6 lg:px-12">
        <div className="max-w-3xl">
          <p className="sjc-eyebrow text-muted-light flex items-center gap-4">
            <span className="sjc-hairline w-8" />
            The Standard
          </p>
          <h2 className="font-display mt-6 text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.015em] text-charcoal">
            Five things we care about,
            <br />
            <span className="italic font-light">quietly and completely.</span>
          </h2>
        </div>

        <ol className="mt-16 md:mt-20 divide-y divide-charcoal/10 border-t border-charcoal/10">
          {PRINCIPLES.map((p, i) => (
            <motion.li
              key={p.n}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: 0.06 * i, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`principle-${p.n}`}
              className="grid grid-cols-1 md:grid-cols-[80px_1fr_2fr] gap-4 md:gap-14 py-10 md:py-14"
            >
              <span className="font-display text-gold/80 italic text-3xl md:text-4xl">
                {p.n}
              </span>
              <h3 className="font-display text-3xl md:text-[40px] leading-[1.1] tracking-tight text-charcoal">
                {p.label}
              </h3>
              <p className="text-muted-light text-base md:text-lg font-light leading-[1.75]">
                {p.body}
              </p>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Testimonials — editorial (placeholders preserved)                  */
/* ------------------------------------------------------------------ */
function TestimonialsEditorial() {
  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="bg-near-black text-warm-white py-24 md:py-36 border-t border-white/10"
    >
      <div className="max-w-[92rem] mx-auto px-6 lg:px-12">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-end mb-14 md:mb-20">
          <div>
            <p className="sjc-eyebrow text-gold flex items-center gap-4">
              <span className="sjc-hairline w-8" />
              In their words
            </p>
            <h2
              data-testid="testimonials-heading"
              className="font-display mt-6 text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.015em]"
            >
              The judgement
              <br />
              <span className="italic font-light">of those we drive.</span>
            </h2>
          </div>
          <p className="text-warm-white/60 text-base md:text-lg font-light leading-[1.75] max-w-md md:justify-self-end">
            A small selection of feedback from clients who&apos;ve travelled with us —
            corporate, family, and everything in between.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {TESTIMONIALS.map((t, i) => (
            <motion.figure
              key={t.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.08 * i, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`testimonial-${t.id}`}
              className="border border-white/8 bg-surface-dark p-8 md:p-10"
            >
              <div className="flex gap-1 text-gold">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} className="w-3 h-3 fill-current" strokeWidth={0} />
                ))}
              </div>
              <blockquote
                className="mt-6 text-warm-white text-xl md:text-2xl leading-[1.5] italic font-light"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-8 pt-6 border-t border-white/8 flex items-center gap-4">
                <span className="w-10 h-10 border border-gold/40 text-warm-white grid place-items-center font-display italic text-base">
                  {t.name.charAt(0)}
                </span>
                <div>
                  <p className="text-sm font-medium text-warm-white">{t.name}</p>
                  <p className="mt-0.5 text-[11px] tracking-[0.22em] uppercase text-warm-white/45 font-medium">
                    {t.role}
                  </p>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  About closing — restrained ivory booking invitation                */
/* ------------------------------------------------------------------ */
function AboutClosing() {
  return (
    <section
      data-testid="about-closing-section"
      className="bg-ivory py-24 md:py-36 border-t border-charcoal/10"
    >
      <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
        <p className="sjc-eyebrow text-muted-light flex items-center justify-center gap-4">
          <span className="sjc-hairline w-8" />
          Ready when you are
          <span className="sjc-hairline w-8" />
        </p>
        <h2 className="font-display mt-8 text-4xl md:text-5xl lg:text-[60px] leading-[1.05] tracking-[-0.015em] text-charcoal">
          Reserve a journey with{" "}
          <span className="italic font-light">Sydney James.</span>
        </h2>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-8">
          <Link
            to="/book"
            data-testid="about-closing-cta"
            className="group inline-flex items-center gap-3 bg-midnight text-warm-white px-8 py-4 text-[11px] tracking-[0.28em] uppercase font-semibold transition-all duration-500 hover:bg-gold hover:text-midnight"
          >
            <span>Book Your Journey</span>
            <span className="w-2 h-2 rounded-full bg-warm-white group-hover:bg-midnight transition-transform duration-500 group-hover:translate-x-1" />
          </Link>
          <a
            href="tel:+61400000000"
            className="group flex items-center gap-4 text-charcoal/85 hover:text-charcoal transition-colors duration-300"
          >
            <span className="w-8 h-px bg-gold transition-all duration-500 group-hover:w-12" />
            <div className="text-left">
              <p className="sjc-eyebrow text-muted-light/70">Or speak with us</p>
              <p className="mt-1 font-display text-xl md:text-2xl tracking-tight">
                +61 400 000 000
              </p>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
